import { v4 as uuidv4 } from 'uuid';
import {StudyPlanData, WeeklyPlan, StudyPlanTask, StudyPlanGoal, SkillBenchmark, UserData, Skills, TestAttempt, SkillName, 
    categories, skillFrequency, CategoryName} from '../UserAndProfile/UserContext';
import {estimateMathScore, estimateReadingScore, readingScoreTable, mathScoreTable, findScore, ScoreRange} from '../dashboard-components/sat-score-estimation'; // Adjust path as needed
import { parseISO } from 'date-fns';

// --- Configuration Interfaces ---

//*************SEE LINE 105 to determine if the # of questions needed is too high due to using lower diagnostic test score. */

export interface StudyPlanConfig {
  testDate: string; // ISO Date string YYYY-MM-DD
  targetScoreMath: number;
  targetScoreRw: number;
  sessionsPerWeek: number;
  minutesPerSession: number;
  userPreferredTimes: string[];
  userId: string; // Needed for potential future async operations or logging
}

// --- Helper Functions ---

/**
 * Calculates the number of weeks between two dates.
 */
const calculateWeeksBetween = (startDate: Date, endDate: Date): number => {
  const msInWeek = 1000 * 60 * 60 * 24 * 7;
  const diff = endDate.getTime() - startDate.getTime();
  return Math.max(1, Math.ceil(diff / msInWeek)); // Ensure at least 1 week
};

/**
 * Gets the start date (Monday) of the week for a given date.
 */
const getWeekStartDate = (date: Date): Date => {
  const day = date.getDay(); // 0 = Sunday, 1 = Monday, ...
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(date.setDate(diff));
};

/**
 * Adds days to a date.
 */
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Formats a Date object to 'YYYY-MM-DD' string.
 */
const formatDateISO = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Finds the approximate percentage required for a target score using the score tables.
 * This is a simplified reverse lookup.
 */
const findTargetPercentage = (targetScore: number, table: ScoreRange[]): number => {
    // Find the first range where the *higher* bound meets or exceeds the target
    const targetRange = table.find(row => row.higher >= targetScore);
    if (targetRange) {
        // Find the previous range's threshold to estimate the target percentage
        const index = table.indexOf(targetRange);
        const previousThreshold = index > 0 ? table[index - 1].threshold : 0;
        // Average the threshold of the target range and the previous one as an estimate
        return (targetRange.threshold + previousThreshold) / 2;
    }
    // If target is very high or not found, aim for a high percentage
    return 0.95;
};

export const getCurrentWeek = (studyPlan: StudyPlanData, today: Date): WeeklyPlan | null => {
  const weeklyPlans = studyPlan.weeklyPlans;
  return weeklyPlans?.find(week => {
    const startDate = parseISO(week.startDate);
    const endDate = parseISO(week.endDate);
  // Adjust end date to be end of the day for comparison
  endDate.setHours(23, 59, 59, 999);
  return today >= startDate && today <= endDate;
}) || weeklyPlans?.[0]; // Fallback to first week if none current
};

export const isStudyPlanStale = (studyPlan: StudyPlanData): boolean => {
  const testDate = parseISO(studyPlan.testDate);
  testDate.setHours(23, 59, 59, 999);
  return new Date() > testDate;
};

/** Called once when a full practice test is finished — increments counters and auto-completes the task. */
export const completeFullPracticeTestInPlan = (studyPlan: StudyPlanData): StudyPlanData => {
  const updated = JSON.parse(JSON.stringify(studyPlan)) as StudyPlanData;
  const currentWeek = getCurrentWeek(updated, new Date());
  if (!currentWeek) return updated;

  const weekIndex = updated.weeklyPlans.findIndex(w => w.weekNumber === currentWeek.weekNumber);
  if (weekIndex === -1) return updated;

  const week = updated.weeklyPlans[weekIndex];
  week.fullTestsCompleted = (week.fullTestsCompleted || 0) + 1;

  if (updated.overallPracticeTestGoal) {
    updated.overallPracticeTestGoal.current = (updated.overallPracticeTestGoal.current || 0) + 1;
  }

  const now = new Date().toISOString();
  const testTask = week.tasks.find(t => t.type === 'full-practice-test' && !t.isCompleted);
  if (testTask) {
    testTask.isCompleted = true;
    testTask.completionDate = now;
  }

  return updated;
};

export const getTaskRoute = (task: StudyPlanTask): string | null => {
  switch (task.type) {
    case 'full-practice-test':
    case 'practice-test-section':
      return '/full-test';
    case 'skill-practice':
      return task.skill
        ? `/skill-practice?skill=${encodeURIComponent(task.skill)}&difficulty=Medium`
        : '/skill-practice';
    case 'question-review':
      return '/review-missed-questions';
    case 'custom':
      return task.skill
        ? `/skill-practice?skill=${encodeURIComponent(task.skill)}&difficulty=Medium`
        : '/sat';
    default:
      return null;
  }
};

/** Find active linked student from parent's referredUsers */
export const getLinkedStudentRef = (user: UserData) =>
  user.referredUsers?.find(r =>
    r.role === 'student' && r.referralStatus === 'active' && r.linkedUserFirebaseId
  );

export const getSkillLevel = (accuracy: number): { label: string; color: 'error' | 'warning' | 'success' } => {
  if (accuracy >= 90) return { label: 'Mastered', color: 'success' };
  if (accuracy >= 80) return { label: 'Proficient', color: 'success' };
  if (accuracy >= 60) return { label: 'Developing', color: 'warning' };
  return { label: 'Learning', color: 'error' };
};

export const getWeeklyPaceMessage = (currentWeek: WeeklyPlan, today: Date = new Date()): string => {
  const goal = currentWeek.weeklySkillsPracticeQuestionGoal;
  if (!goal?.target) return '';

  const weekStart = parseISO(currentWeek.startDate);
  const weekEnd = parseISO(currentWeek.endDate);
  const totalDays = Math.max(1, Math.ceil((weekEnd.getTime() - weekStart.getTime()) / 86400000) + 1);
  const dayIndex = Math.min(totalDays, Math.max(1, Math.ceil((today.getTime() - weekStart.getTime()) / 86400000) + 1));
  const expectedPct = dayIndex / totalDays;
  const actualPct = goal.current / goal.target;

  if (actualPct >= 1) return "You hit your weekly goal — great work!";
  if (actualPct >= expectedPct * 0.9) return "You're on track this week.";
  if (actualPct >= expectedPct * 0.5) return `Almost there — ${goal.target - goal.current} more questions to go.`;
  return `Let's get going — try ${Math.max(5, Math.ceil(goal.target * 0.1))} questions today.`;
};

/** Pending task for a skill (optionally filtered by type). Used by Chat study-plan nudges. */
export const getPendingTaskForSkill = (
  studyPlan: StudyPlanData,
  skill: string,
  type?: StudyPlanTask['type']
): StudyPlanTask | null => {
  const currentWeek = getCurrentWeek(studyPlan, new Date());
  if (!currentWeek) return null;
  return (
    currentWeek.tasks.find(
      (t) =>
        !t.isCompleted &&
        t.skill === skill &&
        (type ? t.type === type : true)
    ) || null
  );
};

/** Weekly skill-practice goal progress for the current week. */
export const getWeeklyGoalProgress = (
  currentWeek: WeeklyPlan
): { current: number; target: number; pct: number } => {
  const goal = currentWeek.weeklySkillsPracticeQuestionGoal;
  const current = goal?.current || 0;
  const target = goal?.target || 0;
  const pct = target > 0 ? (current / target) * 100 : 0;
  return { current, target, pct };
};

export const getTodaysMission = (user: UserData): { title: string; route: string; subtitle?: string } | null => {
  if (!user.studyPlan) return null;
  const currentWeek = getCurrentWeek(user.studyPlan, new Date());
  if (!currentWeek) return null;

  const pendingTest = currentWeek.tasks.find(t => t.type === 'full-practice-test' && !t.isCompleted);
  if (pendingTest) {
    return { title: pendingTest.title, route: getTaskRoute(pendingTest) || '/full-test', subtitle: 'Practice test week' };
  }

  const skillTask = currentWeek.tasks.find(t => t.type === 'skill-practice' && !t.isCompleted && t.skill);
  if (skillTask?.skill) {
    return {
      title: `Practice ${skillTask.skill}`,
      route: getTaskRoute(skillTask) || '/skill-practice',
      subtitle: skillTask.title,
    };
  }

  const skillsGoal = currentWeek.weeklySkillsPracticeQuestionGoal;
  if (skillsGoal.current < skillsGoal.target) {
    const remaining = skillsGoal.target - skillsGoal.current;
    return {
      title: `Complete ${remaining} more practice question${remaining !== 1 ? 's' : ''}`,
      route: '/skill-practice',
      subtitle: `${skillsGoal.current}/${skillsGoal.target} done this week`,
    };
  }

  const reviewGoal = currentWeek.weeklyReviewMissedQuestionGoal;
  if (reviewGoal.current < reviewGoal.target) {
    return {
      title: 'Review missed questions',
      route: '/review-missed-questions',
      subtitle: `${reviewGoal.current}/${reviewGoal.target} reviewed`,
    };
  }

  return null;
};

/** On week rollover: snapshot accuracyChange and update streak fields */
export const advanceStudyPlanWeekIfNeeded = (
  studyPlan: StudyPlanData,
  user: UserData
): { studyPlan: StudyPlanData; userUpdates: Partial<UserData>; changed: boolean } => {
  const currentWeek = getCurrentWeek(studyPlan, new Date());
  if (!currentWeek) return { studyPlan, userUpdates: {}, changed: false };

  const currentWeekNum = currentWeek.weekNumber;
  const lastKnown = studyPlan.lastKnownWeekNumber;

  if (!lastKnown) {
    return {
      studyPlan: { ...studyPlan, lastKnownWeekNumber: currentWeekNum },
      userUpdates: {},
      changed: true,
    };
  }

  if (currentWeekNum <= lastKnown) {
    return { studyPlan, userUpdates: {}, changed: false };
  }

  const updated = JSON.parse(JSON.stringify(studyPlan)) as StudyPlanData;
  const prevWeek = updated.weeklyPlans.find(w => w.weekNumber === lastKnown);
  const prevPrevWeek = updated.weeklyPlans.find(w => w.weekNumber === lastKnown - 1);

  if (prevWeek?.skillProgress && prevPrevWeek?.skillProgress) {
    Object.entries(prevWeek.skillProgress).forEach(([skill, data]) => {
      const prior = prevPrevWeek.skillProgress?.[skill];
      if (prior && prior.attempted >= 2) {
        data.accuracyChange = data.accuracyRate - prior.accuracyRate;
      }
    });
  }

  const userUpdates: Partial<UserData> = {};
  const meaningfulWeek =
    (prevWeek?.activeDates?.length || 0) >= 3 || (prevWeek?.questionsAttempted || 0) >= 30;

  if (meaningfulWeek) {
    const newStreak = (user.currentStreak || 0) + 1;
    userUpdates.currentStreak = newStreak;
    userUpdates.longestStreak = Math.max(user.longestStreak || 0, newStreak);
    if (prevWeek) prevWeek.streak = newStreak;
  } else {
    userUpdates.currentStreak = 0;
  }

  updated.lastKnownWeekNumber = currentWeekNum;
  return { studyPlan: updated, userUpdates, changed: true };
};

export const getParentStatusFromWeek = (currentWeek: WeeklyPlan | null): { label: string; color: 'success' | 'warning' | 'error' } => {
  if (!currentWeek) return { label: 'No activity yet', color: 'error' };

  const daysActive = currentWeek.activeDates?.length || 0;
  const skillPct = currentWeek.weeklySkillsPracticeQuestionGoal?.target
    ? (currentWeek.weeklySkillsPracticeQuestionGoal.current / currentWeek.weeklySkillsPracticeQuestionGoal.target) * 100
    : 0;

  if (daysActive >= 3 && skillPct >= 50) return { label: 'On track', color: 'success' };
  if (daysActive >= 1 || skillPct > 0) return { label: 'Needs encouragement', color: 'warning' };
  return { label: 'Inactive this week', color: 'error' };
};

/** Compare skill accuracy week-over-week (client-side mirror of functions/skillAnalysis) */
export const getSkillsImprovedThisWeek = (user: UserData, currentWeekNumber: number, minAttempts = 4) => {
  if (!user.studyPlan?.weeklyPlans) return [];

  const currentWeek = user.studyPlan.weeklyPlans.find(w => w.weekNumber === currentWeekNumber);
  const previousWeek = user.studyPlan.weeklyPlans.find(w => w.weekNumber === currentWeekNumber - 1);
  if (!currentWeek?.skillProgress || !previousWeek?.skillProgress) return [];

  const improvements: Array<{ skill: string; difficulty: string; change: number }> = [];

  Object.entries(currentWeek.skillProgress).forEach(([skillName, currentData]) => {
    if (currentData.attempted < minAttempts) return;
    const previousData = previousWeek.skillProgress?.[skillName];
    if (!previousData || previousData.attempted < 2) return;

    const difficulties = currentData.difficulty;
    const mostPracticed = Object.entries(difficulties)
      .map(([level, data]) => ({ level, attempted: data.attempted }))
      .sort((a, b) => b.attempted - a.attempted)[0];

    improvements.push({
      skill: skillName,
      difficulty: mostPracticed?.level || 'medium',
      change: currentData.accuracyRate - previousData.accuracyRate,
    });
  });

  return improvements.sort((a, b) => b.change - a.change).slice(0, 3);
};

// --- Main Generation Function ---

const funTasksList = [
  "Achieve a 3-day study streak!",
  "Tell a friend or your parents about an SAT concept you learned this week.",
  "Review your toughest missed question from last week and explain the solution aloud to yourself.",
      "Share the SAT prep app with a friend - they get a 14-day free trial!",
  "Find a new quiet spot to study for at least one session this week.",
  "Reward yourself for meeting your study session goal this week!",
  "Write or use the microphone to ask at least 5 custom questions to the AI tutor."
];

export const generateStudyPlan = (config: StudyPlanConfig,
  userSkills: Skills | null, // Current overall skills (assumed post-diagnostic or latest)
  testAttempts: TestAttempt[] | null // To find diagnostic results
): StudyPlanData | null => {

  const now = new Date();
  const testDate = new Date(config.testDate + 'T00:00:00'); // Ensure parsing as local date

  if (testDate <= now) {
    console.error("Test date must be in the future.");
    return null; // Or throw an error
  }

  const totalWeeks = calculateWeeksBetween(now, testDate);

  // --- 1. Estimate Initial Scores (from Diagnostic) ---
  const completedDiagnostics = testAttempts
      ?.filter(attempt => attempt.testType === 'diagnostic' && attempt.completed) // Use boolean 'completed'
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // Sort descending by date

  const diagnosticAttempt = completedDiagnostics && completedDiagnostics.length > 0
      ? completedDiagnostics[0]
      : null;

  let initialMathScore = 550; // Default fallback
  let initialRwScore = 550;   // Default fallback
  // We will use the userSkills passed into the function as the basis if diagnostic scores aren't directly available

  if (diagnosticAttempt) {
      // Use scores directly from the attempt if available, otherwise keep fallback
      // *************NOTE: ...Scores?.lower is very conservative, possibly increasing the # questions needed to be intimidating. REVISIT THIS in May 2025!
      //Here's the midpoint of the range: "?? diagnosticAttempt.mathScore"  and "?? diagnosticAttempt.readingWritingScore"
      initialMathScore = diagnosticAttempt.diagnosticMathScores?.lower ?? initialMathScore;
      initialRwScore = diagnosticAttempt.diagnosticReadingWritingScores?.lower ?? initialRwScore;
      // Note: We don't get skills snapshot from the attempt based on current types
      console.log("Used diagnosticAttempt to estimate Math score of ", initialMathScore, "and Reading Writing score of ", initialRwScore);
  }

  // --- 2. Estimate Total Work ---
  // Use Math.round on initial scores for gap calculation
  const scoreGapMath = Math.max(0, config.targetScoreMath - Math.round(initialMathScore));
  const scoreGapRw = Math.max(0, config.targetScoreRw - Math.round(initialRwScore));

  // Heuristic: Questions needed per point increase (NEEDS TUNING). 3-4 is my guess as of 4/22/2025.
  const questionsPerPointMath = 3;
  const questionsPerPointRw = 3;

  let totalQuestionsNeeded = Math.ceil(
    (scoreGapMath * questionsPerPointMath) + (scoreGapRw * questionsPerPointRw)
  );
  totalQuestionsNeeded = totalQuestionsNeeded + totalQuestionsNeeded % 10; // Round up to nearest 10
  if (totalQuestionsNeeded < 100) {
    totalQuestionsNeeded = 100;
  }

  const totalSkillPracticeQuestionsNeeded = Math.ceil(totalQuestionsNeeded * 0.90); // Adjusted to 90%
  const totalQuestionReviewQuestionsNeeded = Math.ceil(totalQuestionsNeeded * 0.10); // Adjusted to 10%

  const overallSkillPracticeQuestionGoal: StudyPlanGoal = {
    target: totalSkillPracticeQuestionsNeeded > 90 ? totalSkillPracticeQuestionsNeeded : 30, // Minimum questions
    current: 0,
    unit: 'questions'
  };

  const overallQuestionReviewGoal: StudyPlanGoal = {
    target: totalQuestionReviewQuestionsNeeded > 10 ? totalQuestionReviewQuestionsNeeded : 5, // Minimum questions
    current: 0,
    unit: 'questions'
  };

  const totalPracticeTestsNeeded = Math.min(Math.max(1, Math.floor(totalWeeks / 3)), 3); // 1-3 tests, every 3 weeks
  const overallPracticeTestGoal: StudyPlanGoal = {
    target: totalPracticeTestsNeeded,
    current: 0,
    unit: 'tests'
  };

  // --- 3. Determine Skill Benchmarks ---
  const targetMathPercent = findTargetPercentage(config.targetScoreMath, mathScoreTable);
  const targetRwPercent = findTargetPercentage(config.targetScoreRw, readingScoreTable);

  const skillBenchmarks: { [skillKey: string]: SkillBenchmark | null } = {};
  Object.keys(categories).forEach(category => {
    const targetPercent = category === "Math" ? targetMathPercent : targetRwPercent;
    categories[category as CategoryName].forEach(skillName => {
      const skillKey = skillName.toLowerCase().replace(/ /g, '_');
      skillBenchmarks[skillKey] = {
        targetPercentage: Math.round(targetPercent * 100) // Store as whole number e.g., 85
      };
    });
  });

  // --- 4. Generate Weekly Plans ---
  const weeklyPlans: WeeklyPlan[] = [];
  const questionsPerWeek = totalWeeks > 0 ? Math.ceil(totalQuestionsNeeded / totalWeeks) : totalQuestionsNeeded;
  let testWeekCounter = 0;

  const mathSkills = [...categories.Math];
  const rwSkills = [...categories["Reading and Writing"]];
  let mathSkillCycleIndex = 0;
  let rwSkillCycleIndex = 0;
  let funTaskCycleIndex = 0;


  if (mathSkills.length === 0 && rwSkills.length === 0) {
      console.error("No skills defined in categories. Cannot generate tasks.");
      return null; // Cannot proceed without skills
  }

  for (let i = 0; i < totalWeeks; i++) {
    const weekStartDate = getWeekStartDate(addDays(now, i * 7));
    const weekEndDate = addDays(weekStartDate, 6);
    const weekNumber = i + 1;
    const tasks: StudyPlanTask[] = [];
    const weeklyNumberOfSkillsPracticeQuestions = Math.ceil(questionsPerWeek * .9); // This is a sub-goal, overall is 0.9
    const weeklyNumberOfReviewMissedQuestions = Math.ceil(questionsPerWeek * .1); // This is a sub-goal, overall is 0.1

    const weeklySkillsPracticeQuestionGoal: StudyPlanGoal = {
      target: weeklyNumberOfSkillsPracticeQuestions,
      current: 0,
      unit: 'questions'
    };

    const weeklyReviewMissedQuestionGoal: StudyPlanGoal = {
      target: weeklyNumberOfReviewMissedQuestions,
      current: 0,
      unit: 'questions'
    };

    const weeklySessionGoal: StudyPlanGoal = {
        target: config.sessionsPerWeek,
        current: 0,
        unit: 'sessions'
    };

    // Add Punctuation Task in Week 1
    if (weekNumber === 1) {
      tasks.push({
        id: uuidv4(),
        createdDate: new Date().toISOString(),
        dueDate: formatDateISO(addDays(weekStartDate, 2)), // e.g., Due Wednesday of Week 1
        completionDate: null,
        title: "Use AI Chat to Learn Punctuation Rules: Semicolon, Comma, Colons, and More",
        description: "Focus on Semicolons, Colons, and Commas. Ask the AI tutor to explain the rules and give you examples. This is an easy win!",
        type: "custom",
        durationMinutes: 30,
        skill: "Punctuation" as SkillName, // Cast to SkillName
        isCompleted: false,
        isUserCreated: false,
      });
    }

    // Schedule practice test (e.g., every 3rd week, or the final week)
    const isTestWeek = (weekNumber % 3 === 0) && overallPracticeTestGoal.target > testWeekCounter;

    // Add practice test task if it's a test week
    if (isTestWeek) {
        const testDueDate = addDays(weekStartDate, 6); // Schedule test for Sunday
        tasks.push({
            id: uuidv4(),
            createdDate: new Date().toISOString(),
            dueDate: formatDateISO(testDueDate),
            completionDate: null,
            title: `Complete Full Practice Test #${testWeekCounter + 1}`,
            description: "Simulate real test conditions. Review your results thoroughly afterwards.",
            type: 'full-practice-test',
            durationMinutes: 134, // Official digital SAT duration
            skill: null,
            isCompleted: false,
            isUserCreated: false,
        });
        testWeekCounter++;
    }

    // Add "Master a Skill" and "Fun/Varied" tasks if diagnostic is completed
    if (diagnosticAttempt) {
      // Master a Math Skill
      if (mathSkills.length > 0) {
        const mathSkillToMaster = mathSkills[mathSkillCycleIndex % mathSkills.length];
        tasks.push({
          id: uuidv4(),
          createdDate: new Date().toISOString(),
          dueDate: formatDateISO(addDays(weekStartDate, 1)), // e.g., Due Tuesday
          completionDate: null,
          title: `Master Math Skill: ${mathSkillToMaster}`,
          description: `Focus on understanding core concepts of ${mathSkillToMaster}. Use practice questions and AI chat for help. Aim for 80%+ accuracy on related practice problems.`,
          type: 'skill-practice',
          durationMinutes: config.minutesPerSession,
          skill: mathSkillToMaster,
          isCompleted: false,
          isUserCreated: false,
        });
        mathSkillCycleIndex++;
      }

      // Master a Reading & Writing Skill
      if (rwSkills.length > 0) {
        const rwSkillToMaster = rwSkills[rwSkillCycleIndex % rwSkills.length];
        tasks.push({
          id: uuidv4(),
          createdDate: new Date().toISOString(),
          dueDate: formatDateISO(addDays(weekStartDate, 3)), // e.g., Due Thursday
          completionDate: null,
          title: `Master R&W Skill: ${rwSkillToMaster}`,
          description: `Focus on understanding core concepts and strategies for ${rwSkillToMaster}. Use practice questions and AI chat. Aim for 80%+ accuracy on related practice problems.`,
          type: 'skill-practice',
          durationMinutes: config.minutesPerSession,
          skill: rwSkillToMaster,
          isCompleted: false,
          isUserCreated: false,
        });
        rwSkillCycleIndex++;
      }

      // Fun/Varied Task
      if (funTasksList.length > 0) {
        const funTask = funTasksList[funTaskCycleIndex % funTasksList.length];
        tasks.push({
          id: uuidv4(),
          createdDate: new Date().toISOString(),
          dueDate: formatDateISO(addDays(weekStartDate, 5)), // e.g., Due Saturday
          completionDate: null,
          title: funTask,
          description: "Engage with your SAT prep in a new way!",
          type: 'custom',
          durationMinutes: null,
          skill: null,
          isCompleted: false,
          isUserCreated: false,
        });
        funTaskCycleIndex++;
      }
    }
    // Removed old console.log("Task: ", tasks[0]); as tasks array can be empty or different now.
    // Removed commented out skill practice loop and related logic (questionsAssignedThisWeek etc.)

    weeklyPlans.push({
      weekNumber,
      startDate: formatDateISO(weekStartDate),
      endDate: formatDateISO(weekEndDate),
      weeklySkillsPracticeQuestionGoal,
      weeklyReviewMissedQuestionGoal,
      weeklySessionGoal,
      tasks, // Contains the newly structured tasks
    });
  }

  // --- 5. Construct Final Plan ---
  const studyPlan: StudyPlanData = {
    createdAt: new Date().toISOString(),
    testDate: config.testDate,
    targetScoreMath: config.targetScoreMath,
    targetScoreRw: config.targetScoreRw,
    initialDiagnosticScoreMath: Math.round(initialMathScore),
    initialDiagnosticScoreRw: Math.round(initialRwScore),
    userPreferredTimes: config.userPreferredTimes,
    overallSkillPracticeQuestionGoal,
    overallQuestionReviewGoal,
    overallPracticeTestGoal,
    weeklyPlans,
    skillBenchmarks,
  };

  return studyPlan;
};